import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, of, switchMap, throwError } from 'rxjs';

import { AuthFacade } from '../../authentication/data-access/facades/auth.facade';
import { ME_PERMISSIONS_PATH } from '../menu/domain/menu-api-paths.constant';
import { NotificationService } from '../services/notification.service';
import { APP_CONFIG } from '../tokens/app-config.token';
import { ApiErrorResponse } from './api-error-response.model';
import { TOO_MANY_REQUESTS_MESSAGE } from './api-error.util';
import { SKIP_ALL_ERROR_NOTIFICATIONS, SKIP_ERROR_NOTIFICATION_STATUSES } from './skip-error-notification.token';

const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/password-reset/request',
  '/auth/password-reset/verify',
  '/auth/password-reset/confirm',
  '/auth/change-password',
];

const PERMISSION_DENIED_MESSAGE = 'No tienes permiso para realizar esta acción.';

/** class-validator responde varios mensajes por request; se muestra el primero. */
function firstMessage(message: string | string[] | undefined): string | undefined {
  return Array.isArray(message) ? message[0] : message;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authFacade = inject(AuthFacade);
  const notificationService = inject(NotificationService);
  const router = inject(Router);
  const config = inject(APP_CONFIG);

  const isApiRequest = req.url.startsWith(config.apiUrl);
  const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => req.url === `${config.apiUrl}${path}`);
  const isPermissionsEndpoint = req.url === `${config.apiUrl}${ME_PERMISSIONS_PATH}`;
  const token = authFacade.token();
  const isSameSession = authFacade.trackSession();

  const authorizedReq =
    token && isApiRequest ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorizedReq).pipe(
    catchError((error: unknown) => {
      const isUnauthorized = error instanceof HttpErrorResponse && error.status === 401;
      if (!isUnauthorized || !isApiRequest || isAuthEndpoint) {
        if (isApiRequest && error instanceof HttpErrorResponse) {
          const skipAll = req.context.get(SKIP_ALL_ERROR_NOTIFICATIONS);
          const skipStatuses = req.context.get(SKIP_ERROR_NOTIFICATION_STATUSES);
          if (!skipAll && !skipStatuses.includes(error.status)) {
            if (error.status === 403) {
              // Un 403 tardío de una sesión ya cerrada o reemplazada no le corresponde a quien está ahora.
              if (!isSameSession()) return throwError(() => error);
              notificationService.error(error.status, PERMISSION_DENIED_MESSAGE);
              // Un 403 en la propia recarga de permisos no debe volver a recargarlos (recursión).
              if (!isPermissionsEndpoint) void authFacade.reloadPermissions();
            } else if (error.status === 429) {
              notificationService.error(error.status, TOO_MANY_REQUESTS_MESSAGE);
            } else {
              const body = error.error as Partial<ApiErrorResponse> | null;
              notificationService.error(error.status, firstMessage(body?.message));
            }
          }
        }
        return throwError(() => error);
      }

      // Sin token no hay sesión que renovar, y si la sesión con la que salió la request ya se cerró o
      // se reemplazó (logout o login de otro usuario), refrescar con una cookie aún viva reabriría la
      // sesión anterior o reintentaría como el usuario nuevo. restoreSession no pasa por aquí: llama a
      // /auth/refresh directamente, que es un AUTH_ENDPOINT.
      if (!authorizedReq.headers.has('Authorization') || !isSameSession()) {
        return throwError(() => error);
      }

      const hasSessionExpired = authFacade.trackSessionExpiry();
      // Misma sesión con otro token: otra request ya la refrescó mientras esta viajaba.
      const alreadyRefreshed = authFacade.token() !== token;
      const renewal$ = alreadyRefreshed ? of(true) : from(authFacade.refreshSession());

      return renewal$.pipe(
        switchMap((refreshed) => {
          if (!refreshed) {
            // Solo si la sesión de esta request murió por su refresh y nada la reemplazó: tras un logout
            // (que ya navega) o el login de otro usuario, mandar al login sacaría a quien está ahora.
            if (hasSessionExpired()) router.navigateByUrl('/auth/ingresar');
            return throwError(() => error);
          }
          if (!isSameSession()) return throwError(() => error);

          const retriedReq = req.clone({ setHeaders: { Authorization: `Bearer ${authFacade.token()}` } });
          return next(retriedReq).pipe(
            catchError((retryError: unknown) => {
              // Un 401 tras refrescar el token no es un token vencido (el refresh ya funcionó) sino una
              // regla de negocio del backend (ej. UsuarioSinTransportadoraException en
              // GET /viajes/mis-viajes) — se trata igual que una sesión inválida: se cierra sesión,
              // salvo que esa sesión ya se haya cerrado o reemplazado mientras el reintento viajaba.
              const isUnauthorizedRetry = retryError instanceof HttpErrorResponse && retryError.status === 401;
              if (isUnauthorizedRetry && isSameSession()) {
                void authFacade.logout();
                router.navigateByUrl('/auth/ingresar');
              }
              return throwError(() => retryError);
            }),
          );
        }),
      );
    }),
  );
};
